import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Snackbar, Alert, Divider, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, CardContent, Tooltip, InputAdornment,
} from '@mui/material';
import { Visibility, Search, Refresh } from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

const kycColor = { pending: 'warning', approved: 'success', rejected: 'error', review: 'info' };

export default function KYC() {
  const [rows, setRows]           = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [page, setPage]           = useState(0);
  const [pageSize, setPageSize]   = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [kycDetail, setKycDetail] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [remarks, setRemarks]     = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [snack, setSnack]         = useState({ open: false, msg: '', sev: 'success' });
  const [aadhaarNum, setAadhaarNum] = useState('');
  const [panNum, setPanNum]       = useState('');

  // ── Fetch all KYC records once ────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getKYCList, {
      page: 1, per_page: 500, status: '',
    });
    if (res?.code === '200') {
      setRows((res.kyc ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── Apply local filters (search + status) ────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      rows.filter((r) => {
        const matchSearch =
          !q ||
          r.user_name?.toLowerCase().includes(q) ||
          r.user_phone?.includes(q);
        const matchStatus = !statusFilter || r.status === statusFilter;
        return matchSearch && matchStatus;
      })
    );
    setPage(0);
  }, [search, statusFilter, rows]);

  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const handleView = async (row) => {
    setSelected(row);
    setDialogOpen(true);
    setDetailLoading(true);
    setKycDetail(null); setUnassigned([]); setSelectedVehicle(''); setRemarks(''); 
    setAadhaarNum(''); setPanNum('');
    const [dRes, uRes] = await Promise.all([
      InvokeApi(Config.base_url + apiList.getKYCDetail, { id: row.id }),
      InvokeApi(Config.base_url + apiList.getUnassignedVehicles, {}),
    ]);
    setKycDetail(dRes?.kyc ?? null);
    setUnassigned(uRes?.vehicles ?? []);
    setAadhaarNum(dRes?.kyc?.aadhaar_number ?? '');
    setPanNum(dRes?.kyc?.pan_number ?? '');
    setDetailLoading(false);
  };

  const handleApprove = async () => {
    // Optionally validate Aadhaar and PAN before approval
    if (aadhaarNum && !/^\d{12}$/.test(aadhaarNum)) {
      setSnack({ open: true, msg: 'Aadhaar must be 12 digits.', sev: 'warning' });
      return;
    }
    if (panNum && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNum)) {
      setSnack({ open: true, msg: 'PAN format invalid (e.g., ABCDE1234F).', sev: 'warning' });
      return;
    }
    setActionLoading('approve');
    const res = await InvokeApi(Config.base_url + apiList.approveKYC, { id: selected.id });
    setActionLoading('');
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'KYC Approved successfully.', sev: 'success' });
      setDialogOpen(false); fetchList();
    } else { setSnack({ open: true, msg: res?.message || 'Approve failed.', sev: 'error' }); }
  };

  const handleReject = async () => {
    if (!remarks.trim()) { setSnack({ open: true, msg: 'Please enter rejection remarks.', sev: 'warning' }); return; }
    setActionLoading('reject');
    const res = await InvokeApi(Config.base_url + apiList.rejectKYC, { id: selected.id, remarks });
    setActionLoading('');
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'KYC Rejected.', sev: 'info' });
      setDialogOpen(false); fetchList();
    } else { setSnack({ open: true, msg: res?.message || 'Reject failed.', sev: 'error' }); }
  };

  const handleAssign = async () => {
    if (!selectedVehicle) { setSnack({ open: true, msg: 'Please select a vehicle.', sev: 'warning' }); return; }
    setActionLoading('assign');
    const res = await InvokeApi(Config.base_url + apiList.assignVehicle, { user_id: selected.user_id, vehicle_id: selectedVehicle });
    setActionLoading('');
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'Vehicle assigned successfully.', sev: 'success' });
      setDialogOpen(false); fetchList();
    } else { setSnack({ open: true, msg: res?.message || 'Assign failed.', sev: 'error' }); }
  };

  const docSrc = (path) => path ? (path.startsWith('http') ? path : `${Config.base_url}/${path}`) : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">KYC Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} submission{filtered.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchList()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters — same layout as Users page */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>KYC Status</InputLabel>
            <Select
              value={statusFilter}
              label="KYC Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="review">Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchList()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#ID', 'User', 'Phone', 'KYC Status', 'Vehicle Assigned', 'Submitted', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No KYC submissions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={`#${row.id}`}
                            size="small"
                            sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 34, height: 34,
                                background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                                color: '#fff', fontWeight: 700, fontSize: 13,
                              }}
                            >
                              {(row.user_name || 'U')[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600} fontSize={13}>
                                {row.user_name || 'Unknown'}
                              </Typography>
                              <Typography fontSize={11} color="text.secondary">
                                ID: {row.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {row.user_phone || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || 'N/A'}
                            color={kycColor[row.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {row.vehicle_number
                            ? <Chip label={row.vehicle_number} variant="outlined" color="primary" size="small" />
                            : <Typography variant="caption" color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleView(row)} sx={{ color: '#1a237e' }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
            />
          </>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>
          KYC Details — {selected?.user_name}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            : <Grid container spacing={3}>
                {/* User info */}
                <Grid item xs={12} sm={5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 52, height: 52, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}>
                      {(kycDetail?.user_name || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{kycDetail?.user_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{kycDetail?.user_phone}</Typography>
                      <Chip label={kycDetail?.status || 'N/A'} color={kycColor[kycDetail?.status] || 'default'} size="small" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  {[
                    ['Email',     kycDetail?.user_email],
                    ['Submitted', kycDetail?.submitted_at ? new Date(kycDetail.submitted_at).toLocaleDateString() : '—'],
                    ['Remarks',   kycDetail?.remarks],
                  ].map(([label, value]) => value && (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
                    </Box>
                  ))}
                </Grid>

                {/* Documents */}
                <Grid item xs={12} sm={7}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Documents</Typography>
                  <Grid container spacing={1.5}>
                    {[
                      ['Aadhaar Front', kycDetail?.aadhaar_front],
                      ['Aadhaar Back',  kycDetail?.aadhaar_back],
                      ['PAN Card',      kycDetail?.pan_card],
                      ['DL Front',      kycDetail?.dl_front],
                      ['DL Back',       kycDetail?.dl_back],
                      ['Selfie',        kycDetail?.selfie],
                    ].map(([label, path]) => (
                      <Grid item xs={6} key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        {docSrc(path)
                          ? <Box component="a" href={docSrc(path)} target="_blank" rel="noreferrer">
                              <Box
                                component="img" src={docSrc(path)} alt={label}
                                sx={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 1, border: '1px solid #E2E8F0', cursor: 'pointer', display: 'block', mt: 0.5 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </Box>
                          : <Box sx={{ width: '100%', height: 90, bgcolor: '#f1f5f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">No image</Typography>
                            </Box>
                        }
                      </Grid>
                    ))}
                  </Grid>

                  {/* Document Numbers — for duplicate detection */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Document Numbers</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <TextField
                      size="small" fullWidth
                      label="Aadhaar Number"
                      placeholder="12-digit Aadhaar (extracted or manual)"
                      value={aadhaarNum}
                      onChange={(e) => setAadhaarNum(e.target.value)}
                      disabled={kycDetail?.status === 'approved'}
                    />
                    <TextField
                      size="small" fullWidth
                      label="PAN Number"
                      placeholder="10-char PAN (extracted or manual)"
                      value={panNum}
                      onChange={(e) => setPanNum(e.target.value)}
                      disabled={kycDetail?.status === 'approved'}
                    />
                  </Box>

                  {/* Assign Vehicle */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Assign Vehicle</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Select Vehicle</InputLabel>
                      <Select value={selectedVehicle} label="Select Vehicle" onChange={(e) => setSelectedVehicle(e.target.value)}>
                        {unassigned.map((v) => (
                          <MenuItem key={v.id ?? v.vehicle_id} value={v.id ?? v.vehicle_id}>
                            {v.vehicle_number} — {v.model}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={handleAssign}
                      disabled={actionLoading === 'assign' || !selectedVehicle}
                      size="small"
                      sx={{ borderColor: '#1a237e', color: '#1a237e' }}
                    >
                      {actionLoading === 'assign' ? <CircularProgress size={16} /> : 'Assign'}
                    </Button>
                  </Box>

                  {/* Remarks */}
                  <Divider sx={{ my: 2 }} />
                  <TextField
                    fullWidth multiline rows={2} size="small"
                    label="Rejection Remarks (required to reject)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </Grid>
              </Grid>
          }
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button variant="outlined" color="error" onClick={handleReject} disabled={!!actionLoading}>
            {actionLoading === 'reject' ? <CircularProgress size={16} /> : 'Reject'}
          </Button>
          <Button
            variant="contained" color="success" onClick={handleApprove} disabled={!!actionLoading}
            sx={{ fontWeight: 700 }}
          >
            {actionLoading === 'approve' ? <CircularProgress size={16} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
